import { type GetApiNotificationsQueryParams } from '@/shared/api'
import { createPollingModel } from '@/shared/lib/query'
import { combine, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

import {
  notificationCreate,
  notificationDelete,
  notificationUpdate,
} from '../api'
import {
  type NotificationsQueryModelWithPagination,
  type NotificationsSortState,
  createNotificationsQueryModel,
} from '../lib/notifications-query-model'

interface NotificationsTableGateProps {
  initialFilters?: GetApiNotificationsQueryParams
  initialPage?: number
  initialPageSize?: number
  initialSearch?: string
  initialSort?: NotificationsSortState
  polling?: boolean
}
export const NotificationsTableGate = createGate<NotificationsTableGateProps>()

export const NotificationsTableNotificationsQueryModel =
  createNotificationsQueryModel({
    usePagination: true,
    accumulate: false,
  }) as NotificationsQueryModelWithPagination
const { availableSet, unavailableSet } =
  NotificationsTableNotificationsQueryModel
export const {
  //   Query
  query: notificationsTableQuery,
  //   Init
  $filters: $notificationsTableFilters,
  filtersInit: notificationsTableFiltersInit,
  filtersChanged: notificationsTableFiltersChanged,
  filtersReset: notificationsTableFiltersReset,
  //   Search
  $search: $notificationsTableSearch,
  searchChanged: notificationsTableSearchChanged,
  searchReset: notificationsTableSearchReset,
  //   Sort
  $sort: $notificationsTableSort,
  sortChanged: notificationsTableSortChanged,
  sortReset: notificationsTableSortReset,
  //   Pagination
  $page: $notificationsTablePage,
  pageChanged: notificationsTablePageChanged,
  pageReset: notificationsTablePageReset,
  $pageSize: $notificationsTablePageSize,
  pageSizeChanged: notificationsTablePageSizeChanged,
  //   Refresh
  refreshed: notificationsTableRefreshed,
} = NotificationsTableNotificationsQueryModel

const { startPolling, stopPolling } = createPollingModel(
  notificationsTableRefreshed,
)

sample({
  clock: NotificationsTableGate.open,
  target: availableSet,
})

sample({
  clock: NotificationsTableGate.open,
  filter: ({ polling }) => !!polling,
  target: startPolling,
})

// Search input

export const $notificationsTableSearchInputValue = createStore('')
  .on(notificationsTableSearchChanged, (_, q) => q)
  .reset(notificationsTableSearchReset)

// Setup initial params
// Page
sample({
  clock: NotificationsTableGate.open,
  source: combine(
    NotificationsTableGate.state,
    ({ initialPage }) => initialPage || null,
  ),
  filter: (page) => page !== null,
  target: notificationsTablePageChanged,
})
// Page Size
sample({
  clock: NotificationsTableGate.open,
  source: combine(
    NotificationsTableGate.state,
    ({ initialPageSize }) => initialPageSize || null,
  ),
  filter: (pageSize) => pageSize !== null,
  target: notificationsTablePageSizeChanged,
})
// Search
sample({
  clock: NotificationsTableGate.open,
  source: combine(
    NotificationsTableGate.state,
    ({ initialSearch }) => initialSearch || null,
  ),
  filter: (search) => search !== null,
  target: notificationsTableSearchChanged,
})
// Sort
sample({
  clock: NotificationsTableGate.open,
  source: combine(
    NotificationsTableGate.state,
    ({ initialSort }) => initialSort || null,
  ),
  filter: (sort) => sort !== null,
  target: notificationsTableSortChanged,
})
// Filters
sample({
  source: NotificationsTableGate.state,
  clock: availableSet,
  fn: ({ initialFilters: params }) => params || {},
  target: notificationsTableFiltersInit,
})

// Polling
sample({
  clock: NotificationsTableGate.close,
  target: [unavailableSet, stopPolling],
})

// Decrease page if there is only one record on page and it's being deleted
sample({
  source: combine({
    page: $notificationsTablePage,
    queryData: notificationsTableQuery.$data,
  }),
  clock: notificationDelete.finished.success,
  filter: ({ queryData, page }) =>
    !queryData?.items.length || (queryData?.items.length <= 1 && page > 1),
  fn: ({ page }) => page - 1,
  target: notificationsTablePageChanged,
})

sample({
  clock: [
    notificationCreate.finished.success,
    notificationUpdate.finished.success,
    notificationDelete.finished.success,
  ],
  target: notificationsTableRefreshed,
})
